import { useEffect, useState } from "react";
import { toast } from "sonner";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { PortalShell } from "@/components/PortalShell";

const $ = (c) => `$${(c / 100).toFixed(2)}`;

function InvoicesList() {
  const [invoices, setInvoices] = useState([]);
  const [open, setOpen] = useState(null);
  useEffect(() => {
    api.get("/partner/retail/invoices").then(({ data }) => setInvoices(data.invoices || [])).catch(() => {});
  }, []);
  if (!invoices.length) return <p className="mt-2 text-sm text-slate-500">No invoices issued yet.</p>;
  return (
    <div className="mt-3 space-y-1 text-sm">
      {invoices.map((inv) => (
        <div key={inv.id} className="border-b border-slate-100 py-1.5" data-testid={`partner-invoice-${inv.invoice_number}`}>
          <div className="flex flex-wrap items-center gap-3">
            <button className="font-mono text-cyan-700 underline" onClick={() => setOpen(open === inv.id ? null : inv.id)}>{inv.invoice_number}</button>
            <span className="text-xs px-2 py-0.5 rounded-full bg-slate-100">{inv.status}</span>
            {inv.due_at && <span className="text-slate-500">due {new Date(inv.due_at).toLocaleDateString("en-AU")}</span>}
            <span className="ml-auto font-semibold">{$(inv.total_cents)}</span>
            <button className="text-xs text-cyan-700 underline" data-testid={`partner-invoice-pdf-${inv.invoice_number}`}
              onClick={async () => {
                const r = await api.get(`/partner/retail/invoices/${inv.id}/pdf`, { responseType: "blob" });
                window.open(URL.createObjectURL(r.data), "_blank");
              }}>PDF</button>
          </div>
          {open === inv.id && (
            <div className="mt-2 rounded-lg bg-slate-50 p-2 text-xs" data-testid="partner-invoice-detail">
              {(inv.line_items || []).map((l, i) => (
                <div key={i} className="flex justify-between py-0.5">
                  <span>{l.booking_reference || l.booking_ref} — {l.test_name}</span>
                  <span>{$(l.wholesale_cents)}{l.kit_credit_cents ? ` − ${$(l.kit_credit_cents)} credit` : ""}</span>
                </div>
              ))}
              <div className="text-right font-bold mt-1">Subtotal {$(inv.subtotal_cents)} · Credits {$(inv.kit_credits_cents)} · Due {$(inv.total_cents)}</div>
            </div>
          )}
        </div>
      ))}
    </div>
  );
}

export default function RetailCounter() {
  const [dash, setDash] = useState(null);
  const [tests, setTests] = useState([]);
  const [denied, setDenied] = useState(null);
  const [step, setStep] = useState("kit");
  const [kitCode, setKitCode] = useState("");
  const [noKit, setNoKit] = useState(false);
  const [sampleCollected, setSampleCollected] = useState(false);
  const [approvedBottle, setApprovedBottle] = useState(false);
  const [cust, setCust] = useState({ first_name: "", last_name: "", email: "", mobile: "", address: "" });
  const [testId, setTestId] = useState("");
  const [retailPrice, setRetailPrice] = useState("");
  const [result, setResult] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = () => {
    api.get("/partner/retail/dashboard").then(({ data }) => setDash(data))
      .catch((e) => setDenied(e.response?.data?.detail || "Retail access not enabled"));
    api.get("/partner/retail/tests").then(({ data }) => setTests(data.tests || [])).catch(() => {});
  };
  useEffect(load, []);

  const serialise = async () => {
    try {
      const { data } = await api.post("/partner/retail/kits/serialise", { count: 6 });
      toast.success(`${data.created} kit IDs created`);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  const validateKit = async () => {
    try {
      await api.get(`/partner/retail/kits/${encodeURIComponent(kitCode.trim())}/validate`);
      toast.success("Kit is valid and available");
      setStep("customer");
    } catch (e) { toast.error(e.response?.data?.detail || "Kit not valid"); }
  };

  const create = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/partner/retail/counter-booking", {
        kit_code: noKit ? null : kitCode.trim(),
        customer_has_approved_bottle: noKit,
        sample_already_collected: sampleCollected,
        collected_in_approved_bottle: approvedBottle,
        ...cust,
        test_id: testId,
        customer_retail_price_cents: retailPrice ? Math.round(parseFloat(retailPrice) * 100) : null,
      });
      setResult(data);
      setStep("done");
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Booking failed"); }
    finally { setBusy(false); }
  };

  if (denied) return (
    <PortalShell title="Retail Stockist">
      <div className="max-w-xl mx-auto p-10 text-center" data-testid="retail-denied">
        <p className="mt-3 text-sm text-slate-600">{denied}</p>
      </div>
    </PortalShell>
  );

  return (
    <PortalShell title="Retail Stockist" subtitle="Serialise kit stock, sell AWARE tests over the counter and track invoices.">
    <div className="space-y-6" data-testid="retail-counter-page">
      {!dash && !denied && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="retail-loading">
          {[...Array(4)].map((_, i) => <div key={i} className="h-20 rounded-2xl bg-slate-100 animate-pulse" />)}
        </div>
      )}
      {dash && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="retail-dashboard-cards">
          {[["Kits available", dash.kits_available], ["Awaiting sample", dash.awaiting_sample],
            ["Tests sold", dash.tests_sold], ["Credits", $(dash.credits_cents || 0)]].map(([l, v]) => (
            <div key={l} className="rounded-2xl border border-slate-200 bg-white p-4">
              <div className="text-xs uppercase tracking-wider text-slate-500">{l}</div>
              <div className="text-2xl font-black text-slate-900">{v}</div>
            </div>
          ))}
        </div>
      )}
      {dash?.low_stock && (
        <div className="rounded-xl bg-amber-50 border border-amber-200 p-3 text-sm text-amber-800" data-testid="retail-low-stock">
          Low-stock warning: {dash.kits_available} kits remaining.
          <Button size="sm" variant="outline" className="ml-3" onClick={() => (window.location.href = "/partner/kit-orders")}
            data-testid="retail-order-more">Order More Kits</Button>
        </div>
      )}
      <div className="flex gap-2">
        <Button variant="outline" onClick={serialise} data-testid="retail-serialise-btn">Create 6 Kit IDs (serialise stock)</Button>
      </div>

      {dash && (
        <div className="rounded-3xl border border-slate-200 bg-white p-6" data-testid="retail-invoices-panel">
          <h2 className="text-lg font-bold text-slate-900">Invoices &amp; Credits</h2>
          <InvoicesList />
        </div>
      )}

      <div className="rounded-3xl border border-slate-200 bg-white p-6 space-y-4">
        <h2 className="text-lg font-bold text-slate-900">New Retail Water Test</h2>

        {step === "kit" && (
          <div className="space-y-3" data-testid="retail-step-kit">
            <label className="text-sm font-semibold">Scan or enter Kit ID</label>
            <div className="flex gap-2">
              <Input value={kitCode} onChange={(e) => setKitCode(e.target.value.toUpperCase())}
                placeholder="AW-K8F3P2" data-testid="retail-kit-input" />
              <Button onClick={validateKit} disabled={!kitCode.trim()} data-testid="retail-kit-validate">Validate</Button>
            </div>
            <button className="text-sm text-cyan-700 underline" data-testid="retail-no-kit"
              onClick={() => { setNoKit(true); setStep("customer"); }}>
              Customer brought an approved AWARE/laboratory bottle (no kit consumed)
            </button>
          </div>
        )}

        {step === "customer" && (
          <div className="space-y-3" data-testid="retail-step-customer">
            <div className="grid md:grid-cols-2 gap-3">
              {[["first_name", "First name"], ["last_name", "Surname"], ["email", "Email"],
                ["mobile", "Mobile"], ["address", "Property address"]].map(([k, l]) => (
                <div key={k} className={k === "address" ? "md:col-span-2" : ""}>
                  <label className="text-xs font-semibold text-slate-600">{l}</label>
                  <Input value={cust[k]} onChange={(e) => setCust({ ...cust, [k]: e.target.value })}
                    data-testid={`retail-cust-${k}`} />
                </div>
              ))}
            </div>
            <div className="rounded-xl bg-slate-50 border border-slate-200 p-3 space-y-2 text-sm">
              <label className="flex items-center gap-2">
                <input type="checkbox" checked={sampleCollected} onChange={(e) => setSampleCollected(e.target.checked)}
                  data-testid="retail-sample-collected" />
                Customer already has a collected sample
              </label>
              {sampleCollected && (
                <label className="flex items-center gap-2">
                  <input type="checkbox" checked={approvedBottle} onChange={(e) => setApprovedBottle(e.target.checked)}
                    data-testid="retail-approved-bottle" />
                  It was collected directly into an approved AWARE/laboratory bottle for this test
                </label>
              )}
              {sampleCollected && !approvedBottle && (
                <div className="text-xs text-rose-700 font-medium" data-testid="retail-container-warning">
                  A new approved sample bottle is required. Give the customer the correct AWARE bottle
                  and ask them to recollect. Do not transfer the existing water into the sterile bottle.
                </div>
              )}
            </div>
            <Button onClick={() => setStep("test")} data-testid="retail-to-test"
              disabled={!cust.first_name || !cust.last_name || !cust.email || (sampleCollected && !approvedBottle)}>
              Continue
            </Button>
          </div>
        )}

        {step === "test" && (
          <div className="space-y-3" data-testid="retail-step-test">
            <label className="text-sm font-semibold">Select AWARE test</label>
            <div className="grid md:grid-cols-2 gap-2">
              {tests.map((t) => (
                <button key={t.id} onClick={() => setTestId(t.id)} data-testid={`retail-test-${t.id}`}
                  className={`rounded-xl border p-3 text-left text-sm ${testId === t.id ? "border-cyan-500 bg-cyan-50" : "border-slate-200"}`}>
                  <div className="font-semibold">{t.name}</div>
                  <div className="text-xs text-slate-500">Your AWARE charge: {$(t.wholesale_cents)} (never shown to customer)</div>
                </button>
              ))}
            </div>
            <div>
              <label className="text-xs font-semibold text-slate-600">Your retail price charged to customer (optional, private)</label>
              <Input type="number" step="0.01" value={retailPrice} onChange={(e) => setRetailPrice(e.target.value)}
                placeholder="e.g. 149.00" data-testid="retail-price-input" className="max-w-[200px]" />
            </div>
            <Button onClick={create} disabled={!testId || busy} data-testid="retail-create-booking">
              {busy ? "Creating…" : "Create AWARE Test Booking"}
            </Button>
          </div>
        )}

        {step === "done" && result && (
          <div className="space-y-3" data-testid="retail-step-done">
            <div className="rounded-2xl bg-emerald-50 border border-emerald-200 p-5">
              <div className="text-sm font-semibold text-emerald-800">Booking created</div>
              <div className="text-3xl font-black text-slate-900" data-testid="retail-booking-ref">{result.reference}</div>
              <p className="mt-2 text-sm text-slate-700">{result.instruction}</p>
            </div>
            <Button variant="outline" data-testid="retail-new-sale" onClick={() => {
              setStep("kit"); setKitCode(""); setNoKit(false); setResult(null); setTestId("");
              setCust({ first_name: "", last_name: "", email: "", mobile: "", address: "" });
              setSampleCollected(false); setApprovedBottle(false); setRetailPrice("");
            }}>New Retail Water Test</Button>
          </div>
        )}
      </div>
    </div>
    </PortalShell>
  );
}
